import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Box,
  Flex,
  Button,
  Text,
  useToast,
} from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import { Form, Formik, ErrorMessage } from 'formik';
import React from 'react';

import {
  error as commonError,
  utils as commonUtils,
  date as commonDate,
  finleyAssertUnreachable,
} from '@finley-technologies/common';
import { FinancialData, Util } from '@finley-technologies/finley-types';

import {
  RailzIntegrationFragmentGQLType,
  useFetchAndUploadFinancialDataFromRailzMutation,
} from '../../requests/financialData.gql.generated';
import { colors } from 'packages/common-frontend/colors';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import { SimpleDatePicker } from 'packages/common-frontend/components/SimpleDatePicker';
import { Typography } from 'packages/common-frontend/components/Typography';

type FetchFinancialDataProps = {
  startDate: Util.FullDateStr;
  endDate: Util.FullDateStr;
  reportFrequency: FinancialData.ReportFrequency;
};

const FetchFinancialDataForm = ({
  railzIntegration,
  onClose,
}: {
  railzIntegration: RailzIntegrationFragmentGQLType;
  onClose: () => void;
}) => {
  const { id: railzIntegrationId, railzServiceName } = railzIntegration;
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const toast = useToast();

  const [
    fetchAndUploadFinancialData,
    { loading: isLoadingFetchData },
  ] = useFetchAndUploadFinancialDataFromRailzMutation({
    onCompleted: ({ dataCount }) => {
      if (dataCount > 0) {
        toast({
          title: `Successfully fetched data from ${commonUtils.toTitleCase(
            railzServiceName,
          )}.`,
          position: 'top',
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        toast({
          title: `Did not properly fetch any data from ${commonUtils.toTitleCase(
            railzServiceName,
          )}. Please contact support if this is unexpected and continues to occur.`,
          position: 'top',
          status: 'warning',
          duration: 7000,
          isClosable: true,
        });
      }
    },
    onError: (error) => {
      Sentry.captureException(
        new commonError.FinleyError(
          `Failed to manually fetch data from Railz for ${railzServiceName} [error:${error.message}]`,
          { metadata: { logMeta: { error, railzIntegration } } },
        ),
      );
      toast({
        title: `An error occurred while fetching data from ${commonUtils.toTitleCase(
          railzServiceName,
        )}. Please contact support@finleycms.com if this continues.`,
        position: 'top',
        status: 'error',
        duration: 6000,
        isClosable: true,
      });
    },
  });

  const onSubmit = async (
    createParams: FetchFinancialDataProps,
  ): Promise<void> => {
    const { startDate, endDate, reportFrequency } = createParams;

    /**
     * Note that validation is already handled at the Formik level below
     */
    const { errors } = await fetchAndUploadFinancialData({
      variables: {
        railzIntegrationId,
        queryParamInputs: {
          reportFrequency,
          accountingMethod: 'accrual',
          startDate,
          endDate,
        },
      },
    });
    if (errors) {
      toast({
        title: 'An error occurred while fetching financial data',
        description: errors.map((err) => err.message).join(', '),
        status: 'error',
        duration: 6000,
        position: 'top',
        isClosable: true,
      });
      return;
    }
    onClose();
  };

  return (
    <Formik<FetchFinancialDataProps>
      initialValues={{
        startDate: commonDate.firstOfPeriod(
          commonDate.addMonths(commonDate.currentDateStr(), -1),
          'month',
        ),
        endDate: commonDate.endOfPeriod(
          commonDate.addMonths(commonDate.currentDateStr(), -1),
          'month',
        ),
        reportFrequency: 'month',
      }}
      onSubmit={(props) => onSubmit(props)}
      validate={(values) => {
        const { startDate, endDate, reportFrequency } = values;
        const error: { [K in keyof typeof values]?: string } = {};

        if (!startDate) {
          error.startDate = 'Please select a start date.';
        }
        if (!endDate) {
          error.endDate = 'Please select a end date.';
        }
        if (commonDate.diffDays(endDate, startDate) < 1) {
          error.endDate = 'End date has to be after start date';
        } else {
          // Want to ensure we only allow for single periods from this fetching
          // mechanism, so we don't have to worry about cross periods, like 2 quarter
          // in a single API pull.
          // This may have downstream effects and is unlikely it would benefit much and
          // is used often, so preventing this for now
          const monthDiff = commonDate.diffMonths({ endDate, startDate });
          switch (reportFrequency) {
            case 'month':
              if (monthDiff !== 0) {
                error.endDate = 'Expected 1 month date range for month period';
              }
              break;
            case 'quarter':
              if (monthDiff !== 2) {
                error.endDate =
                  'Expected 3 month date range for quarter period';
              }
              break;
            case 'year':
              if (monthDiff !== 11) {
                error.endDate = 'Expected 12 month date range for year period';
              }
              break;
            default:
              finleyAssertUnreachable(reportFrequency);
          }
        }

        return error;
      }}
      validateOnChange={false}
    >
      {(formProps) => (
        <Form onSubmit={formProps.handleSubmit}>
          {/* *** Start Date *** */}
          <Box mb="4">
            <Flex alignItems="start" justifyContent="space-between">
              <Flex pt="0.5rem">
                <Typography fontWeight="medium">Start Date</Typography>
              </Flex>
              <SimpleDatePicker
                width="50"
                dateFormat="MMMM d, yyyy"
                tz={clientTimeZone}
                maxDate={new Date()}
                selectedDate={commonDate.fullDateStrToDate(
                  formProps.values.startDate,
                )}
                setSelectedDate={(date: Date | null) => {
                  if (!date) return;
                  const fullDateStr = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  // Always defaulting to beginning of month
                  const firstOfPeriod = commonDate.firstOfPeriod(
                    fullDateStr,
                    'month',
                  );
                  formProps.setFieldValue('startDate', firstOfPeriod);
                }}
              />
            </Flex>
            <ErrorMessage name="startDate">
              {(msg) => (
                <Text textAlign="right" color={colors.error} pr="2" my="1">
                  {msg}
                </Text>
              )}
            </ErrorMessage>
          </Box>

          {/* *** End Date *** */}
          <Box mb="4">
            <Flex alignItems="start" justifyContent="space-between">
              <Flex pt="0.5rem">
                <Typography fontWeight="medium">End Date</Typography>
              </Flex>
              <SimpleDatePicker
                width="50"
                dateFormat="MMMM d, yyyy"
                tz={clientTimeZone}
                selectedDate={commonDate.fullDateStrToDate(
                  formProps.values.endDate,
                )}
                setSelectedDate={(date: Date | null) => {
                  if (!date) return;
                  const fullDateStr = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  // Always defaulting to end of month
                  const endOfPeriod = commonDate.endOfPeriod(
                    fullDateStr,
                    'month',
                  );
                  formProps.setFieldValue('endDate', endOfPeriod);
                }}
              />
            </Flex>
            <ErrorMessage name="endDate">
              {(msg) => (
                <Text textAlign="right" color={colors.error} pr="2" my="1">
                  {msg}
                </Text>
              )}
            </ErrorMessage>
          </Box>

          {/* *** Report Frequency *** */}
          <Box mb="10">
            <Flex alignItems="center" justifyContent="space-between">
              <Text mr="3" color={colors.black[95]} fontWeight={500}>
                Report Period
              </Text>
              <Box w="52">
                <Select<FinancialData.ReportFrequency>
                  value={formProps.values.reportFrequency}
                  options={formatSelectOptions(
                    FinancialData.ReportFrequencyIterable,
                    'title',
                  )}
                  onChange={(val) => {
                    if (val) {
                      formProps.setFieldValue('reportFrequency', val);
                    }
                  }}
                />
              </Box>
            </Flex>
            <ErrorMessage name="reportFrequency">
              {(msg) => (
                <Text textAlign="right" color={colors.error} pr="2" my="1">
                  {msg}
                </Text>
              )}
            </ErrorMessage>
          </Box>

          <Flex alignItems="center" justifyContent="flex-end">
            <Button onClick={onClose} mr="8">
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={isLoadingFetchData}
              variant="solid"
              colorScheme="aurora"
            >
              Fetch
            </Button>
          </Flex>
        </Form>
      )}
    </Formik>
  );
};

export const RailzFetchFinancialDataModal = ({
  isOpen,
  onClose,
  railzIntegration,
}: {
  isOpen: boolean;
  onClose: () => void;
  railzIntegration: RailzIntegrationFragmentGQLType;
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent px="3" pt="3">
        <ModalHeader>
          <Text mb="10">Fetch Financial Data</Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody w="100%" pb="5">
          <FetchFinancialDataForm
            railzIntegration={railzIntegration}
            onClose={onClose}
          />
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
